/**
 * Equity Calculator View
 *
 * Interactive options/equity simulator. The current grant model and eleven
 * educational slides are followed by a calculator where the user adjusts
 * share price and share count to see current vs. simulated total value.
 *
 * Ported from the Retool app `apps/People/equity calculator`.
 */
import { useEffect, useMemo, useState } from 'react';
import { Slider } from '@finn-auto/design-system/atoms/slider';
import { Input } from '@finn-auto/design-system/atoms/input';
import { logAnalyticsEvent } from '../../../analytics/nativeAnalytics';

/** Current share price in EUR, confirmed by People Operations in September 2026. */
const CURRENT_PRICE_EUR = 5.394;

const EQUITY_GUIDE_URL =
  'https://finn.atlassian.net/wiki/spaces/HR/pages/3168108584/Guide+to+our+Employee+Equity+Program';

const GRANT_TYPES = [
  {
    title: 'Initial grant',
    description: 'Permanent employees receive a grant when joining FINN, based on role and level.',
  },
  {
    title: 'Promotion grant',
    description:
      'A promotion grant covers the difference between the equity bands of the old and new role or level.',
  },
  {
    title: 'Rating-based grant',
    description:
      'Allocated in November from the average of both bi-annual performance ratings and the available equity pool.',
  },
  {
    title: 'Key Contributor Bonus',
    description:
      'A separate grant for extraordinary impact or culture role-model contributions, independent of other grants.',
  },
] as const;

/** Retool image UUIDs, rendered via the FINN CDN. Order matches the Retool app. */
const SLIDE_IMAGE_UUIDS = [
  '58c4a387-5d0b-4d0e-8a90-d5f96316900a',
  '333dd4ce-e33a-48d3-a1c6-97a7842ff06b',
  '92902473-4289-4673-af23-eedbfff1dc0a',
  'e3bfe4f2-f29a-4a0a-82c1-f677da1b27c6',
  '8788ef8f-97cb-438f-be3e-f3603e17d05a',
  'a241630b-ebcf-4b44-b099-213982f5f2f1',
  'fc9f6e18-977b-47c3-8e63-a1cc22e98e92',
  '387dc5a9-52a8-435d-b1ed-c6f21b359ef1',
  'c0219e17-8982-4fa5-ad2d-3fe658121508',
  'b584c855-29ec-4b8a-a371-9efd21751266',
  '3d63875f-8773-41df-98e7-e1bec09a54d9',
];

const eurFormatter = new Intl.NumberFormat('de-DE', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 2,
});

const sharePriceFormatter = new Intl.NumberFormat('de-DE', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 4,
  maximumFractionDigits: 4,
});

const pctFormatter = new Intl.NumberFormat('en-US', {
  style: 'percent',
  maximumFractionDigits: 1,
});

interface StatCardProps {
  label: string;
  value: string;
  secondary?: string;
  secondaryTone?: 'up' | 'down' | 'neutral';
  emphasized?: boolean;
}

function StatCard({
  label,
  value,
  secondary,
  secondaryTone = 'neutral',
  emphasized,
}: StatCardProps) {
  const toneClass =
    secondaryTone === 'up' ? 'text-green' : secondaryTone === 'down' ? 'text-red' : 'text-iron';
  return (
    <div
      className="flex flex-col gap-1 border border-pearl bg-white p-4"
      style={{ borderRadius: '4px' }}
    >
      <span className="text-xs font-medium uppercase tracking-wider text-iron">{label}</span>
      <span className={`text-2xl font-semibold ${emphasized ? 'text-trustedBlue' : 'text-black'}`}>
        {value}
      </span>
      {secondary && (
        <span className={`text-xs font-medium ${toneClass}`}>
          {secondaryTone === 'up' ? '▲ ' : secondaryTone === 'down' ? '▼ ' : ''}
          {secondary}
        </span>
      )}
    </div>
  );
}

export default function EquityCalculatorView() {
  const [simulatedPrice, setSimulatedPrice] = useState(CURRENT_PRICE_EUR);
  const [shares, setShares] = useState(0);

  useEffect(() => {
    void logAnalyticsEvent('equity_calculator_viewed');
  }, []);

  const { currentTotal, simulatedTotal, pctChange } = useMemo(() => {
    const curr = CURRENT_PRICE_EUR * shares;
    const sim = simulatedPrice * shares;
    const pct = (simulatedPrice - CURRENT_PRICE_EUR) / CURRENT_PRICE_EUR;
    return { currentTotal: curr, simulatedTotal: sim, pctChange: pct };
  }, [simulatedPrice, shares]);

  const pctTone = pctChange > 0 ? 'up' : pctChange < 0 ? 'down' : 'neutral';

  return (
    <div className="h-full overflow-auto">
      <div className="max-w-3xl mx-auto flex flex-col gap-6 py-6">
        <h1 className="text-2xl font-semibold text-black">Equity Program</h1>

        <section
          className="flex flex-col gap-4 border border-pearl bg-snow p-6"
          style={{ borderRadius: '4px' }}
        >
          <div className="flex flex-col gap-1">
            <span className="text-xs font-semibold uppercase tracking-wider text-trustedBlue">
              Grant model since January 2026
            </span>
            <h2 className="text-xl font-semibold text-black">How additional equity is granted</h2>
            <p className="text-sm text-iron">
              FINN combines milestone grants with performance-based grants. Rating-based grants vest
              over four years with no cliff and start vesting in the month of allocation.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {GRANT_TYPES.map((grant) => (
              <article
                key={grant.title}
                className="flex flex-col gap-1 border border-pearl bg-white p-4"
              >
                <h3 className="text-sm font-semibold text-black">{grant.title}</h3>
                <p className="text-sm text-iron">{grant.description}</p>
              </article>
            ))}
          </div>

          <p className="text-xs text-iron">
            2026 transition: grants replacing January–October anniversary grants vest
            retrospectively from the original anniversary month.{' '}
            <a
              href={EQUITY_GUIDE_URL}
              target="_blank"
              rel="noreferrer"
              className="font-semibold text-trustedBlue underline hover:text-trustedBluePressed"
            >
              Read the Employee Equity Program Guide
            </a>
          </p>
        </section>

        {/* Slide deck */}
        <div className="flex flex-col gap-3">
          {SLIDE_IMAGE_UUIDS.map((uuid) => (
            <img
              key={uuid}
              src={`https://one.finn.auto/api/file/${uuid}`}
              alt=""
              className="w-full h-auto"
              loading="lazy"
            />
          ))}
        </div>

        {/* Simulator */}
        <section
          className="flex flex-col gap-4 border border-pearl bg-snow p-6"
          style={{ borderRadius: '4px' }}
        >
          <div>
            <h2 className="text-xl font-semibold text-trustedBlue">Options Simulator</h2>
            <p className="text-sm text-iron">Play around to see what's possible!</p>
          </div>

          {/* Current value */}
          <div>
            <h3 className="mb-2 text-sm font-semibold text-black">Current Value</h3>
            <div className="grid grid-cols-2 gap-3">
              <StatCard label="Current Total Value" value={eurFormatter.format(currentTotal)} />
              <StatCard
                label="Current Share Price"
                value={sharePriceFormatter.format(CURRENT_PRICE_EUR)}
              />
            </div>
          </div>

          {/* Controls */}
          <div
            className="grid grid-cols-1 gap-4 border border-pearl bg-white p-4 md:grid-cols-2"
            style={{ borderRadius: '4px' }}
          >
            <div className="flex flex-col gap-2">
              <label className="text-xs font-medium uppercase tracking-wider text-iron">
                Share Price
              </label>
              <Slider
                min={CURRENT_PRICE_EUR}
                max={CURRENT_PRICE_EUR * 5}
                step={0.0001}
                value={[simulatedPrice]}
                onValueChange={(values: number[]) =>
                  setSimulatedPrice(values[0] ?? CURRENT_PRICE_EUR)
                }
              />
              <span className="text-sm font-medium text-black">
                {sharePriceFormatter.format(simulatedPrice)}
              </span>
            </div>
            <div className="flex flex-col gap-2">
              <label
                htmlFor="equity-shares-input"
                className="text-xs font-medium uppercase tracking-wider text-iron"
              >
                Number of Shares
              </label>
              <Input
                id="equity-shares-input"
                type="number"
                min={0}
                step={1}
                value={shares}
                onChange={(e) => {
                  const parsed = parseInt(e.target.value, 10);
                  setShares(Number.isFinite(parsed) && parsed >= 0 ? parsed : 0);
                }}
              />
            </div>
          </div>

          {/* Simulated value */}
          <div>
            <h3 className="text-sm font-semibold text-trustedBlue mb-2">Simulated Value</h3>
            <div className="grid grid-cols-2 gap-3">
              <StatCard
                label="Simulated Share Price"
                value={sharePriceFormatter.format(simulatedPrice)}
                secondary={pctFormatter.format(pctChange)}
                secondaryTone={pctTone}
              />
              <StatCard
                label="Simulated Total Value"
                value={eurFormatter.format(simulatedTotal)}
                emphasized
              />
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
